import { getAuth } from 'firebase/auth';
import { captureAsyncError } from '../../../utils/errorHandler';
import { ItemListType } from '../../../types/common';

export interface RuleSuggestion {
  field_name: string;
  current_rule: string;
  suggested_rule: string;
  reason: string;
}

export interface SuggestPatternRuleRequest {
  resultId: string;
  patternId: string;
  patternItemList: ItemListType[];
}

export interface SuggestPatternRuleResponse {
  success: boolean;
  suggestions: RuleSuggestion[];
  error?: { message: string };
}

function convertItemListForApi(itemList: ItemListType[]): Array<{
  id: string;
  parent_value: string;
  extraction_rule: string;
  children: Array<{ id: string; child_value: string; extraction_rule: string }>;
}> {
  return (
    itemList
      // AI読取の対象外（手入力欄・計算フィールド）は取得ルールが効かないため渡さない。
      // 子パターンの提案（handleSuggestSubPatternRules）と同じ絞り込みにする（#3314）
      .filter(item => item.is_active !== false && item.is_ocr_target !== false)
      .map(item => ({
        id: item.id,
        parent_value: item.parentValue,
        extraction_rule: item.extractionRule ?? '',
        children: (item.children ?? [])
          .filter(child => child.is_active !== false && child.is_ocr_target !== false)
          .map(child => ({
            id: child.id,
            child_value: child.childValue,
            extraction_rule: child.extractionRule ?? '',
          })),
      }))
  );
}

export const handleSuggestPatternRule = captureAsyncError(
  async (request: SuggestPatternRuleRequest): Promise<SuggestPatternRuleResponse> => {
    const auth = getAuth();
    const user = auth.currentUser;
    if (!user) {
      throw new Error('ユーザー認証が必要です');
    }

    const idToken = await user.getIdToken();
    const functionUrl = process.env.REACT_APP_FUNCTION_URL;
    if (!functionUrl) {
      throw new Error('REACT_APP_FUNCTION_URL environment variable is not set');
    }

    const response = await fetch(`${functionUrl}/suggestPatternRuleImprovement`, {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${idToken}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        resultId: request.resultId,
        patternId: request.patternId,
        patternItemList: convertItemListForApi(request.patternItemList),
      }),
    });

    if (!response.ok) {
      throw new Error('AI改善提案の取得に失敗しました。もう一度お試しください。');
    }

    const result = (await response.json()) as SuggestPatternRuleResponse;
    if (result.error) {
      throw new Error(result.error.message || 'AI改善提案の取得に失敗しました。');
    }

    return result;
  },
);
